import { useEffect, useState } from 'react';
import { db } from '@siaedts/shared/lib/supabase';
import type { AboutContent } from '@siaedts/shared';
import { useLanguage } from '../i18n';

type AboutForm = Omit<AboutContent, 'updated_at'>;
type AboutSection = 'story' | 'descriptions' | 'contact' | 'values' | 'milestones' | 'certifications';

const emptyForm: AboutForm = {
  id: 'main',
  hero_description: "A modern, professional, and reliable agro-livestock and veterinary company based in Macenta, Guinea's Forest Region.",
  company_paragraphs: ["SIAEDTS-SARL is an industrial agro-livestock company headquartered in Macenta Centre, in the Bamala district of Guinea's Forest Region, N'zérékoré.", 'Our work covers industry in all its forms, the management and study of cultivable land, and applied research in livestock.', 'We also import and export veterinary products and agricultural inputs in Guinea, while investing in training, health, and safety. Our ambition is to become a reference in the agro-livestock and veterinary sector.'],
  story_image_url: null,
  story_video_url: null,
  values_subtitle: 'The principles that guide everything we do.',
  values: [
    {
      title: 'Integrity First',
      description: 'We build lasting relationships through honesty, transparency, and ethical business practices in every interaction.',
    },
    {
      title: 'Customer Success',
      description: 'Your productivity is our priority. We measure our success by the success of the farmers we serve.',
    },
    {
      title: 'Innovation',
      description: 'We continuously adopt efficient, sustainable farming solutions.',
    },
    {
      title: 'Community',
      description: 'We are deeply rooted in the agricultural community, supporting local farms and future farmers.',
    },
  ],
  milestones: [
    {
      year: '2009',
      title: 'Company Founded',
      description: 'Started as a small family dealership with 3 employees',
    },
    {
      year: '2012',
      title: 'John Deere Partnership',
      description: 'Became authorized John Deere dealer for the region',
    },
    {
      year: '2024',
      title: 'Valley Irrigation Added',
      description: 'Expanded our portfolio with irrigation solutions',
    },
    {
      year: '2026',
      title: '15 Year Anniversary',
      description: 'Celebrating 15 years serving the agricultural community',
    },
  ],
  milestones_subtitle: 'Key milestones in our 15+ year history.',
  team_subtitle: 'Experienced professionals dedicated to your success.',
  ecosystem_subtitle: 'From poultry and veterinary products to fresh produce, aquaculture, and trusted national and international partners.',
  leadership_title: 'Meet Our Leadership',
  ecosystem_title: 'Our products & ecosystem',
  partners_title: 'Our Trusted Partners',
  certifications_title: 'Certifications & Recognition',
  certifications_subtitle: 'Industry-recognized excellence in service and sales',
  certifications: [
    { title: 'John Deere', subtitle: 'Platinum Dealer', icon: '🏆' },
    { title: 'Case IH', subtitle: 'Gold Level Dealer', icon: '🥇' },
    { title: 'Valley', subtitle: 'Certified Installer', icon: '💧' },
    { title: 'Kubota', subtitle: 'Elite Dealer', icon: '⭐' },
  ],
  whatsapp_number: '',
  social_links: { facebook: '', twitter: '', linkedin: '', instagram: '' },
};

function cleanAboutForm(content: AboutContent): AboutForm {
  return {
    ...emptyForm,
    ...content,
    company_paragraphs: content.company_paragraphs.filter((paragraph) => paragraph.trim().length > 0),
    values: content.values.filter((value) => value.title.trim().length > 0 || value.description.trim().length > 0),
    milestones: content.milestones.filter((milestone) => milestone.year.trim().length > 0 || milestone.title.trim().length > 0 || milestone.description.trim().length > 0),
    certifications: content.certifications.filter((certification) => certification.title.trim().length > 0 || certification.subtitle.trim().length > 0),
  };
}

export default function AboutEditor() {
  const { language } = useLanguage();
  const french = language === 'fr';
  const [form, setForm] = useState<AboutForm>(emptyForm);
  const [savingSection, setSavingSection] = useState<AboutSection | null>(null);
  const [sectionMessages, setSectionMessages] = useState<Partial<Record<AboutSection, string>>>({});

  useEffect(() => {
    void db.about
      .get()
      .then((content) => {
        if (!content) return;
        setForm(cleanAboutForm(content));
      })
      .catch(() =>
        setSectionMessages({
          story: french ? 'Le contenu À propos n’est pas encore disponible. Appliquez la migration Supabase, puis rechargez cette page.' : 'About content is not available yet. Apply the Supabase migration, then reload this page.',
        }),
      );
  }, []);

  async function saveSection(section: AboutSection) {
    setSavingSection(section);
    setSectionMessages((current) => ({ ...current, [section]: '' }));
    try {
      const cleanedForm = cleanAboutForm(form as AboutContent);
      const payload: Partial<Omit<AboutContent, 'id' | 'updated_at'>> =
        section === 'story'
          ? {
              hero_description: cleanedForm.hero_description,
              company_paragraphs: cleanedForm.company_paragraphs,
              story_image_url: cleanedForm.story_image_url,
              story_video_url: cleanedForm.story_video_url,
            }
          : section === 'descriptions'
            ? {
                team_subtitle: cleanedForm.team_subtitle,
                ecosystem_subtitle: cleanedForm.ecosystem_subtitle,
                leadership_title: cleanedForm.leadership_title,
                ecosystem_title: cleanedForm.ecosystem_title,
                partners_title: cleanedForm.partners_title,
                certifications_title: cleanedForm.certifications_title,
                certifications_subtitle: cleanedForm.certifications_subtitle,
              }
            : section === 'contact'
              ? {
                  whatsapp_number: cleanedForm.whatsapp_number,
                  social_links: cleanedForm.social_links,
                }
              : section === 'values'
                ? {
                    values_subtitle: cleanedForm.values_subtitle,
                    values: cleanedForm.values,
                  }
                : section === 'milestones'
                  ? {
                      milestones_subtitle: cleanedForm.milestones_subtitle,
                      milestones: cleanedForm.milestones,
                    }
                  : { certifications: cleanedForm.certifications };
      await db.about.saveSection(payload);
      setSectionMessages((current) => ({
        ...current,
        [section]: french ? 'Section enregistrée.' : 'Section saved.',
      }));
    } catch (error) {
      setSectionMessages((current) => ({
        ...current,
        [section]: error instanceof Error ? error.message : french ? 'Impossible d’enregistrer cette section.' : 'Could not save this section.',
      }));
    } finally {
      setSavingSection(null);
    }
  }

  function sectionSave(section: AboutSection) {
    return (
      <div className="about-section-save">
        <button className="primary-button" type="button" onClick={() => void saveSection(section)} disabled={savingSection !== null}>
          {savingSection === section ? (french ? 'Enregistrement...' : 'Saving...') : french ? 'Enregistrer cette section' : 'Save this section'}
        </button>
        {sectionMessages[section] && (
          <p className="editor-message" role="status">
            {sectionMessages[section]}
          </p>
        )}
      </div>
    );
  }

  const text = french
    ? {
        eyebrow: 'Contenu du site public',
        title: 'À propos',
        hero: 'Présentez l’histoire que les visiteurs découvrent sur SIAEDTS-SARL.',
        story: 'Histoire de l’entreprise',
        storyHint: 'Commencez par la présentation et les médias qui représentent votre entreprise.',
        header: 'Description d’en-tête',
        paragraphs: 'Paragraphes de l’entreprise',
        image: 'URL de l’image de l’histoire',
        video: 'URL de la vidéo de l’histoire',
        sections: 'Descriptions des sections',
        sectionsHint: 'Définissez le texte sous chaque section de la page À propos.',
        values: 'Valeurs fondamentales',
        valuesHint: 'Ajoutez les principes que vous souhaitez présenter.',
        milestones: 'Étapes de l’entreprise',
        milestonesHint: 'Conservez les moments importants de votre histoire.',
        addValue: '+ Ajouter une valeur',
        addMilestone: '+ Ajouter une étape',
        valueTitle: 'Titre de la valeur',
        valueDescription: 'Description de la valeur',
        year: 'Année',
        milestoneTitle: 'Titre de l’étape',
        description: 'Description',
        remove: 'Supprimer',
        save: 'Enregistrer le contenu À propos',
        ready: 'Prêt à publier vos changements ?',
        savedHint: 'Les changements apparaîtront sur la page publique après l’enregistrement.',
      }
    : {
        eyebrow: 'Public website content',
        title: 'About Us',
        hero: 'Shape the story visitors see when they discover SIAEDTS-SARL.',
        story: 'Company story',
        storyHint: 'Start with the introduction and the media that represent your company.',
        header: 'Header description',
        paragraphs: 'Company paragraphs',
        image: 'Story image URL',
        video: 'Story video URL',
        sections: 'Section descriptions',
        sectionsHint: 'Set the supporting text under each About page section.',
        values: 'Core values',
        valuesHint: 'Add the principles you want visitors to see.',
        milestones: 'Company milestones',
        milestonesHint: 'Keep the important moments in your company story.',
        addValue: '+ Add value',
        addMilestone: '+ Add milestone',
        valueTitle: 'Value title',
        valueDescription: 'Value description',
        year: 'Year',
        milestoneTitle: 'Milestone title',
        description: 'Description',
        remove: 'Remove',
        save: 'Save About content',
        ready: 'Ready to publish your changes?',
        savedHint: 'Updates appear on the public About page after saving.',
      };
  return (
    <div className="about-editor-shell">
      <div className="about-editor-hero">
        <div>
          <p className="eyebrow">{text.eyebrow}</p>
          <h2>{text.title}</h2>
          <p>{text.hero}</p>
        </div>
        <div className="about-editor-hero-mark">
          A<span>+</span>
        </div>
      </div>
      <div className="content-editor about-editor-form">
        <div className="about-editor-intro">
          <span className="about-editor-step">01</span>
          <div>
            <h3>{text.story}</h3>
            <p>{text.storyHint}</p>
          </div>
        </div>
        <label>
          {text.header}
          <textarea required rows={3} value={form.hero_description} onChange={(event) => setForm({ ...form, hero_description: event.target.value })} />
        </label>
        <label>
          {text.paragraphs}
          <textarea
            required
            rows={8}
            value={form.company_paragraphs.join('\n\n')}
            onChange={(event) =>
              setForm({
                ...form,
                company_paragraphs: event.target.value
                  .split(/\n\s*\n/)
                  .map((paragraph) => paragraph.trim())
                  .filter(Boolean),
              })
            }
          />
        </label>
        <div className="editor-grid">
          <label>
            {text.image}
            <input
              type="url"
              value={form.story_image_url ?? ''}
              onChange={(event) =>
                setForm({
                  ...form,
                  story_image_url: event.target.value || null,
                })
              }
            />
          </label>
          <label>
            {text.video}
            <input
              type="url"
              value={form.story_video_url ?? ''}
              onChange={(event) =>
                setForm({
                  ...form,
                  story_video_url: event.target.value || null,
                })
              }
            />
          </label>
        </div>
        {sectionSave('story')}
        <section className="about-editor-section">
          <div className="about-editor-intro">
            <span className="about-editor-step">02</span>
            <div>
              <h3>{text.sections}</h3>
              <p>{text.sectionsHint}</p>
            </div>
          </div>
          <div className="about-description-grid">
            <label>
              <span>Leadership title</span>
              <textarea required rows={2} value={form.leadership_title} onChange={(event) => setForm({ ...form, leadership_title: event.target.value })} />
            </label>
            <label>
              <span>Leadership description</span>
              <textarea required rows={2} value={form.team_subtitle} onChange={(event) => setForm({ ...form, team_subtitle: event.target.value })} />
            </label>
            <label>
              <span>Products and ecosystem title</span>
              <textarea required rows={2} value={form.ecosystem_title} onChange={(event) => setForm({ ...form, ecosystem_title: event.target.value })} />
            </label>
            <label>
              <span>Products and ecosystem description</span>
              <textarea required rows={2} value={form.ecosystem_subtitle} onChange={(event) => setForm({ ...form, ecosystem_subtitle: event.target.value })} />
            </label>
            <label>
              <span>Trusted partners title</span>
              <textarea required rows={2} value={form.partners_title} onChange={(event) => setForm({ ...form, partners_title: event.target.value })} />
            </label>
            <label>
              <span>Certifications title</span>
              <textarea required rows={2} value={form.certifications_title} onChange={(event) => setForm({ ...form, certifications_title: event.target.value })} />
            </label>
            <label className="wide-field">
              <span>Certifications description</span>
              <textarea
                required
                rows={2}
                value={form.certifications_subtitle}
                onChange={(event) =>
                  setForm({
                    ...form,
                    certifications_subtitle: event.target.value,
                  })
                }
              />
            </label>
          </div>
          {sectionSave('descriptions')}
        </section>
        <section className="about-editor-section">
          <div className="about-editor-intro">
            <span className="about-editor-step">06</span>
            <div>
              <h3>Contact & social links</h3>
              <p>Manage the WhatsApp number and footer social profiles.</p>
            </div>
          </div>
          <div className="editor-grid">
            <label>
              WhatsApp number
              <input type="tel" placeholder="+224 622 702 486" value={form.whatsapp_number} onChange={(event) => setForm({ ...form, whatsapp_number: event.target.value })} />
            </label>
            <label>
              Facebook URL
              <input
                type="url"
                value={form.social_links.facebook}
                onChange={(event) =>
                  setForm({
                    ...form,
                    social_links: {
                      ...form.social_links,
                      facebook: event.target.value,
                    },
                  })
                }
              />
            </label>
            <label>
              Twitter / X URL
              <input
                type="url"
                value={form.social_links.twitter}
                onChange={(event) =>
                  setForm({
                    ...form,
                    social_links: {
                      ...form.social_links,
                      twitter: event.target.value,
                    },
                  })
                }
              />
            </label>
            <label>
              LinkedIn URL
              <input
                type="url"
                value={form.social_links.linkedin}
                onChange={(event) =>
                  setForm({
                    ...form,
                    social_links: {
                      ...form.social_links,
                      linkedin: event.target.value,
                    },
                  })
                }
              />
            </label>
            <label>
              Instagram URL
              <input
                type="url"
                value={form.social_links.instagram}
                onChange={(event) =>
                  setForm({
                    ...form,
                    social_links: {
                      ...form.social_links,
                      instagram: event.target.value,
                    },
                  })
                }
              />
            </label>
          </div>
          {sectionSave('contact')}
        </section>
        <section className="about-editor-section">
          <div className="about-editor-section-heading">
            <div className="about-editor-intro">
              <span className="about-editor-step">03</span>
              <div>
                <h3>{text.values}</h3>
                <p>{text.valuesHint}</p>
              </div>
            </div>
            <button
              className="secondary-button"
              type="button"
              onClick={() =>
                setForm({
                  ...form,
                  values: [...form.values, { title: '', description: '' }],
                })
              }
            >
              {text.addValue}
            </button>
          </div>
          {form.values.map((value, index) => (
            <div className="about-repeatable-row" key={`value-${index}`}>
              <div className="about-repeatable-fields">
                <label>
                  {text.valueTitle}
                  <input
                    required
                    value={value.title}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        values: form.values.map((item, itemIndex) => (itemIndex === index ? { ...item, title: event.target.value } : item)),
                      })
                    }
                  />
                </label>
                <label>
                  {text.valueDescription}
                  <textarea
                    required
                    rows={3}
                    value={value.description}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        values: form.values.map((item, itemIndex) => (itemIndex === index ? { ...item, description: event.target.value } : item)),
                      })
                    }
                  />
                </label>
              </div>
              <button
                className="remove-button"
                type="button"
                onClick={() =>
                  setForm({
                    ...form,
                    values: form.values.filter((_, itemIndex) => itemIndex !== index),
                  })
                }
              >
                {text.remove}
              </button>
            </div>
          ))}
          {sectionSave('values')}
        </section>
        <section className="about-editor-section">
          <div className="about-editor-section-heading">
            <div className="about-editor-intro">
              <span className="about-editor-step">04</span>
              <div>
                <h3>{text.milestones}</h3>
                <p>{text.milestonesHint}</p>
              </div>
            </div>
            <button
              className="secondary-button"
              type="button"
              onClick={() =>
                setForm({
                  ...form,
                  milestones: [...form.milestones, { year: '', title: '', description: '' }],
                })
              }
            >
              {text.addMilestone}
            </button>
          </div>
          {form.milestones.map((milestone, index) => (
            <div className="about-repeatable-row" key={`milestone-${index}`}>
              <div className="about-repeatable-fields milestone-fields">
                <label>
                  {text.year}
                  <input
                    required
                    value={milestone.year}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        milestones: form.milestones.map((item, itemIndex) => (itemIndex === index ? { ...item, year: event.target.value } : item)),
                      })
                    }
                  />
                </label>
                <label>
                  {text.milestoneTitle}
                  <input
                    required
                    value={milestone.title}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        milestones: form.milestones.map((item, itemIndex) => (itemIndex === index ? { ...item, title: event.target.value } : item)),
                      })
                    }
                  />
                </label>
                <label>
                  {text.description}
                  <textarea
                    required
                    rows={3}
                    value={milestone.description}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        milestones: form.milestones.map((item, itemIndex) => (itemIndex === index ? { ...item, description: event.target.value } : item)),
                      })
                    }
                  />
                </label>
              </div>
              <button
                className="remove-button"
                type="button"
                onClick={() =>
                  setForm({
                    ...form,
                    milestones: form.milestones.filter((_, itemIndex) => itemIndex !== index),
                  })
                }
              >
                {text.remove}
              </button>
            </div>
          ))}
          {sectionSave('milestones')}
        </section>
        <section className="about-editor-section">
          <div className="about-editor-section-heading">
            <div className="about-editor-intro">
              <span className="about-editor-step">05</span>
              <div>
                <h3>Certifications</h3>
                <p>Manage recognition cards shown on the public About page.</p>
              </div>
            </div>
            <button
              className="secondary-button"
              type="button"
              onClick={() =>
                setForm({
                  ...form,
                  certifications: [...form.certifications, { title: '', subtitle: '', icon: '★' }],
                })
              }
            >
              + Add certification
            </button>
          </div>
          {form.certifications.map((certification, index) => (
            <div className="about-repeatable-row" key={`certification-${index}`}>
              <div className="about-repeatable-fields milestone-fields">
                <label>
                  Icon
                  <input
                    required
                    value={certification.icon}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        certifications: form.certifications.map((item, itemIndex) => (itemIndex === index ? { ...item, icon: event.target.value } : item)),
                      })
                    }
                  />
                </label>
                <label>
                  Title
                  <input
                    required
                    value={certification.title}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        certifications: form.certifications.map((item, itemIndex) => (itemIndex === index ? { ...item, title: event.target.value } : item)),
                      })
                    }
                  />
                </label>
                <label>
                  Subtitle
                  <input
                    required
                    value={certification.subtitle}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        certifications: form.certifications.map((item, itemIndex) => (itemIndex === index ? { ...item, subtitle: event.target.value } : item)),
                      })
                    }
                  />
                </label>
              </div>
              <button
                className="remove-button"
                type="button"
                onClick={() =>
                  setForm({
                    ...form,
                    certifications: form.certifications.filter((_, itemIndex) => itemIndex !== index),
                  })
                }
              >
                {text.remove}
              </button>
            </div>
          ))}
          {sectionSave('certifications')}
        </section>
      </div>
    </div>
  );
}
